"use client";

import { Button } from "@/components/atoms/Button";
import SpecializationCard from "@/components/molecules/SpecialisationCard";
import {
  cardClick,
  ctaClick,
  expansionClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useCallback, useRef, useState } from "react";

interface SpecialisationMetadata {
  id: string;
  icon: string;
  text: string;
  alt_icon?: string;
  priority: number;
  card_color: string;
  card_title: string;
  hover_icon: string;
  card_hover_color: string;
  card_title_color: string;
  card_title_hover_color?: string;
  mobile_card_title_color?: string;
  card_description_color: string;
  card_description_hover_color?: string;
  mobile_card_description_color?: string;
  card_cta_text?: string;
  card_cta_text_color?: string;
}

interface SpecialisationData {
  id?: number;
  page_id?: number;
  component_id?: number;
  title: string;
  title_text_color?: string;
  cta_text?: string;
  cta_text_color?: string;
  cta_background_color?: string;
  cta_icon?: string;
  cta_icon_alt?: string;
  background_color?: string;
  variant?: string;
  metadata: SpecialisationMetadata[];
  status?: number;
  is_deleted?: number;
  created_by?: string | null;
  updated_by?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface Specialisation1Props {
  data: {
    data: SpecialisationData;
  };
}

function Specialisation1({ data }: Specialisation1Props) {
  const isMobileView = useIsMobile(768);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const [selected, setSelected] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const ref = useRef<HTMLElement>(null);

  const {
    title = "Specialisations Available",
    title_text_color = "#000000",
    cta_text = "Load More",
    cta_text_color = "#FFFFFF",
    cta_background_color = "#000000",
    cta_icon = "",
    cta_icon_alt = "CTA icon",
    background_color = "#FFFFFF",
    metadata = [],
  } = data?.data || {};

  // Sort metadata by priority
  const sortedMetadata = [...metadata].sort(
    (a, b) => (a.priority || 0) - (b.priority || 0),
  );

  const mobileInitialCount = 3;
  const desktopInitialCount = 6;
  const initialVisibleCount =
    isMobileView === true ? mobileInitialCount : desktopInitialCount;

  const visibleItems = showAll
    ? sortedMetadata
    : sortedMetadata.slice(0, initialVisibleCount);

  const hasMore = !showAll && sortedMetadata.length > initialVisibleCount;

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // Track widget viewed
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "specialisation_section");
  //   }
  // }, []);

  // Handle card click with tracking
  const handleClick = useCallback(
    (index: number, item: SpecialisationMetadata) => {
      // Track card click
      cardClick({
        section: "specialisation_section",
        component: "specialisation_card",
        label: item.card_title || item.text || `Specialisation ${index + 1}`,
        parent: title,
        redirectLink: "",
        pageKey: "",
      });

      if (isMobileView) {
        const wasExpanded = selectedItem === index && selected;
        if (wasExpanded) {
          // Card is being collapsed
          setSelected((prev) => !prev);
          expansionClick({
            section: "specialisation_section",
            component: "specialisation_card",
            label:
              item.card_title || item.text || `Specialisation ${index + 1}`,
            index: String(index),
            entity_name: item.id,
          });
        } else {
          // Card is being expanded
          setSelectedItem(index);
          setSelected(true);
          expansionClick({
            section: "specialisation_section",
            component: "specialisation_card",
            label:
              item.card_title || item.text || `Specialisation ${index + 1}`,
            index: String(index),
            entity_name: item.id,
          });
        }
      }
    },
    [isMobileView, selectedItem, selected, title],
  );

  // Handle Load More button click with tracking
  const handleLoadMore = useCallback(() => {
    setShowAll(true);
    ctaClick({
      section: "specialisation_section",
      component: "load_more_button",
      label: cta_text || "Load More",
      step: "",
      entity: "load_more_button",
    });
  }, [cta_text]);

  return (
    <section
      ref={ref}
      className={cn(
        "flex flex-col items-center justify-center w-full text-center",
        "px-5 py-8 gap-[24px]",
        "max-[767px]:gap-6 max-[767px]:py-0 max-[767px]:pt-10 max-[767px]:pb-[40px]",
        "sm:p-[42px] sm:gap-[34px]",
        "md:px-[54px] md:py-[42px]",
        "lg:px-[80px] lg:py-16",
        "xl:px-[120px] xl:py-[42px]",
      )}
      style={{ backgroundColor: background_color }}
    >
      <div
        className={cn(
          "flex min-w-0 w-full flex-col items-center",
          "max-w-full sm:max-w-[996px]",
          "md:max-w-full md:w-full",
          "lg:max-w-[1270px] xl:max-w-[1510px]",
          "gap-[10px] sm:gap-[34px]",
        )}
      >
        <h2
          className={cn(
            "text-[24px] font-semibold leading-normal text-[#101112]",
            "max-[767px]:line-clamp-3 max-[767px]:min-w-0 max-[767px]:w-full max-[767px]:whitespace-normal",
            "sm:text-[28px] sm:font-semibold sm:leading-normal",
            "tab:line-clamp-3 tab:whitespace-nowrap",
            "type-h3",
          )}
          style={{ color: title_text_color }}
        >
          {title}
        </h2>

        <div
          className={cn(
            "grid w-full",
            "grid-cols-2 gap-x-[14px] gap-y-6",
            "max-[767px]:grid-cols-1 max-[767px]:gap-y-5",
            "sm:grid-cols-3 sm:gap-x-[28px] sm:gap-y-[28px]",
            "md:gap-x-[30px] md:gap-y-[30px]",
            "lg:gap-x-[40px] lg:gap-y-[40px]",
            "xl:gap-x-[60px] xl:gap-y-[60px]",
          )}
        >
          {visibleItems.map((item, index) => {
            const isFlipped =
              Boolean(isMobileView) && selected && selectedItem === index;

            return (
              <SpecializationCard
                key={item.id}
                title={item.card_title}
                description={item.text}
                learnMoreLabel={
                  item.card_cta_text?.trim()
                    ? item.card_cta_text.trim()
                    : "Learn More"
                }
                ctaTextColor={item.card_cta_text_color}
                icon={item.icon}
                hoverIcon={item.hover_icon}
                altIcon={item.alt_icon}
                cardColor={item.card_color}
                cardHoverColor={item.card_hover_color}
                cardTitleColor={item.card_title_color}
                cardTitleHoverColor={item.card_title_hover_color}
                mobileCardTitleColor={item.mobile_card_title_color}
                cardDescriptionColor={item.card_description_color}
                cardDescriptionHoverColor={item.card_description_hover_color}
                mobileCardDescriptionColor={item.mobile_card_description_color}
                isFlipped={isFlipped}
                onClick={() => handleClick(index, item)}
              />
            );
          })}
        </div>
      </div>

      {hasMore && (
        <Button
          onClick={handleLoadMore}
          className={cn(
            "flex items-center justify-center gap-2",
            "min-h-[53px] h-[53px] text-[24px] px-4 rounded-[10px]",
            "sm:gap-[10px] sm:px-6 sm:py-2 sm:text-[24px] sm:font-semibold",
            "md:h-[53px] md:gap-[10px] md:px-6 md:py-2 md:text-[24px] md:font-semibold",
            "lg:h-[53px] lg:gap-[10px] lg:px-6 lg:py-2 lg:text-[24px] lg:font-semibold",
            "xl:h-[53px] xl:gap-[10px] xl:px-6 xl:py-2 xl:text-[24px] xl:font-semibold",
            "type-h6",
          )}
          style={{
            backgroundColor: cta_background_color,
            color: cta_text_color,
          }}
        >
          {cta_text}
          {cta_icon && (
            <div className="relative shrink-0 sm:size-9 md:size-9 lg:size-9 xl:size-9">
              <Image
                src={cta_icon}
                alt={cta_icon_alt}
                fill
                className="object-contain"
              />
            </div>
          )}
        </Button>
      )}
    </section>
  );
}

export default Specialisation1;
